<div class="page">
  <div class="page-head">
    <div>
      <h2>{{ pageTitle() || 'Collect Payment' }}</h2>
      <p>Collect the member's premium. On a verified success the member is marked PAID.</p>
    </div>
    @if (!done() && isAdmin) {
      <p-button label="All Payments" icon="pi pi-list" [outlined]="true" (onClick)="goToList()" />
    }
  </div>

  <!-- Member summary + premium breakup -->
  <div class="member-card">
    @if (loadingMember()) {
      <span class="muted"><i class="pi pi-spin pi-spinner"></i> Loading member…</span>
    } @else {
      <div class="member-id">
        <span class="avatar">{{ (memberName() || 'M').charAt(0) | uppercase }}</span>
        <div>
          <strong>{{ memberName() }}</strong>
          <small class="mono">{{ memberUid() || 'Member #' + memberId }}</small>
          @if (premium().mobile) { <small class="muted">{{ premium().mobile }}</small> }
        </div>
      </div>

      @if (member(); as m) {
        <div class="premium-grid">
          @if (premium().policy) {
            <div class="pcol"><span>Policy</span><strong>{{ premium().policy }}</strong></div>
          }
          @if (premium().coverage) {
            <div class="pcol"><span>Coverage</span><strong>{{ premium().coverage }}</strong></div>
          }
          @if (premium().sumInsured) {
            <div class="pcol"><span>Sum Insured</span><strong>₹{{ premium().sumInsured | number: '1.0-0' }}</strong></div>
          }
          <div class="pcol"><span>Base Premium</span><strong>₹{{ premium().base | number: '1.0-0' }}</strong></div>
          <div class="pcol"><span>GST ({{ premium().gstPer }}%)</span><strong>₹{{ premium().gstAmt | number: '1.0-0' }}</strong></div>
          <div class="pcol total"><span>Total Premium (incl. GST)</span><strong>₹{{ premium().total | number: '1.0-0' }}</strong></div>
        </div>
      }
    }
  </div>

  @if (loadingMember()) {
    <!-- member still loading: the member-card spinner above covers this -->
  }

  <!-- ===================== DONE (just collected) ===================== -->
  @else if (done()) {
    <div class="card form-card">
      <div class="paid-state">
        <i class="pi pi-check-circle"></i>
        <h3>{{ memberName() }} is PAID</h3>
        <p>The premium has been collected and the member row stamped with the paid amount and time.</p>
        @if (paidMode()) { <p-tag [value]="'Paid via ' + paidMode()" severity="success" /> }
        <div class="done-actions">
          <p-button label="View Receipt" icon="pi pi-receipt" [outlined]="true" (onClick)="openReceiptDialog()" />
          @if (coiMemberId()) {
            <p-button label="View Certificate" icon="pi pi-verified" (onClick)="openCoiDialog()" />
          }
        </div>
      </div>
    </div>
  }

  <!-- ===================== ALREADY PAID ===================== -->
  @else if (alreadyPaid()) {
    <div class="card form-card">
      <div class="paid-state">
        <i class="pi pi-check-circle"></i>
        <h3>{{ memberName() }} is already PAID</h3>
        <p>This member's premium has already been collected — there is nothing left to pay.</p>
        @if (isAdmin) { <p-button label="All Payments" icon="pi pi-list" [outlined]="true" (onClick)="goToList()" /> }
      </div>
    </div>
  }

  <!-- ===================== STEP 3 — OTP VERIFY (member not verified) ===================== -->
  @else if (stage() === 'otp') {
    <div class="card form-card">
      <p class="confirm-hint">
        This member isn't verified yet. Verify with the OTP sent to their registered contact, then collect the premium.
      </p>
      @if (sendingOtp()) {
        <span class="muted"><i class="pi pi-spin pi-spinner"></i> Sending OTP…</span>
      } @else {
        <app-otp-step [memberId]="memberId" [channels]="otpChannels()" [expiresInMinutes]="otpExpiry()"
          [otpSent]="otpSent()" [otpMessage]="otpMessage()" (verified)="onOtpVerified()" />
      }
    </div>
  }

  <!-- ===================== STEP 4 — COLLECT PAYMENT (verified + unpaid) ===================== -->
  @else {
    <div class="card form-card">
      <form [formGroup]="payForm" class="pay-form">
        <div class="grid-2">
          <div class="field">
            <label>Amount <span class="req">*</span></label>
            <p-inputNumber formControlName="amount" mode="currency"
              [currency]="payForm.get('currency')?.value || 'INR'" locale="en-IN" [min]="0.01" placeholder="0.00" />
            @if (payForm.get('amount')?.touched && payForm.get('amount')?.invalid) {
              <small class="err">Enter an amount greater than 0</small>
            }
          </div>
          <div class="field">
            <label>Currency</label>
            <input pInputText type="text" formControlName="currency" maxlength="3" placeholder="INR">
          </div>
        </div>

        <div class="grid-2">
          <div class="field">
            <label>Payment Mode</label>
            <p-select formControlName="paymentMode" [options]="modeOptions()" optionLabel="label"
              optionValue="value" [showClear]="true" placeholder="UPI / Card / Cash…" appendTo="body" />
            @if (isOffline) { <small class="hint-muted">Offline mode — recorded directly, no online checkout.</small> }
          </div>
          <div class="field">
            <label>Payer Type <span class="req">*</span></label>
            <p-select formControlName="payerType" [options]="payerOptions()" optionLabel="label"
              optionValue="value" placeholder="Who is paying?" appendTo="body" />
          </div>
        </div>

        @if (isPartnerPayer) {
          <div class="field">
            <label>Partner</label>
            <p-select formControlName="partnerId" [options]="partnerOptions()" optionLabel="label"
              optionValue="value" [filter]="true" filterBy="label" [showClear]="true"
              placeholder="Defaults to the member's partner" appendTo="body" />
            <small class="hint-muted">Leave blank to use the member's own partner.</small>
          </div>
        } @else {
          <div class="field">
            <label>Client Reference</label>
            <input pInputText type="text" formControlName="clientRef" placeholder="Member mobile / client code">
            <small class="hint-muted">Leave blank to use the member's unique id.</small>
          </div>
        }

        @if (!isOffline && gatewayRedirect()) {
          <!-- A redirect gateway leaves the app, so say so before the page vanishes under them. -->
          <p class="pay-note">
            <i class="pi pi-external-link"></i>
            This partner collects through {{ payLabel().replace('Pay with ', '') }} — the customer is
            taken there to pay and returns to the payment result page.
          </p>
        }

        <!-- Offered when the gateway refuses the payment because the member has no e-mail. -->
        <app-member-email-dialog [memberId]="memberId" [memberName]="memberName()"
          [gatewayName]="payLabel().replace('Pay with ', '')" (saved)="onEmailSaved($event)" />

        <div class="modal-actions">
          <!-- Labelled with the gateway the server will actually open, not a hardcoded one. -->
          <p-button [label]="payLabel()"
            [icon]="isOffline ? 'pi pi-check' : 'pi pi-credit-card'" (onClick)="pay()" [loading]="processing()" />
        </div>
      </form>
    </div>
  }
</div>

<!-- ===================== RECEIPT (auto-shown on success) ===================== -->
<p-dialog [visible]="showReceipt()" (visibleChange)="showReceipt.set($event)" [modal]="true"
  [draggable]="true" [resizable]="false" [dismissableMask]="true" header="Payment Receipt"
  styleClass="menu-dialog" [style]="{ width: '720px' }" [breakpoints]="{ '720px': '94vw' }">
  @if (showReceipt()) {
    <app-receipt-view [paymentId]="receiptPaymentId()" />
  }
</p-dialog>

<!-- ===================== CERTIFICATE OF INSURANCE (offered on success) ===================== -->
<p-dialog [visible]="showCoi()" (visibleChange)="showCoi.set($event)" [modal]="true"
  [draggable]="true" [resizable]="false" [dismissableMask]="true" header="Certificate of Insurance"
  styleClass="menu-dialog" [style]="{ width: '900px' }" [breakpoints]="{ '960px': '96vw' }">
  @if (showCoi()) {
    <app-coi-view [memberId]="coiMemberId()" />
  }
</p-dialog>
